import type { ConnectOptions } from '../connect'
import { privateConnection } from './singleplayer'

export interface GuestProfile { uuid: string; wireName: string; displayName: string }
type FetchProfile = typeof fetch

export function parseGuestProfile(value: unknown): GuestProfile {
  const profile = value as Partial<GuestProfile> | null
  if (!profile || typeof profile.uuid !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profile.uuid) || typeof profile.wireName !== 'string' || !/^VR_[0-9a-f]{12}$/.test(profile.wireName) || typeof profile.displayName !== 'string' || !/^[A-Za-z0-9_]{1,16}$/.test(profile.displayName)) throw new Error('The server returned an invalid guest profile. Refresh and try again.')
  return { uuid: profile.uuid, wireName: profile.wireName, displayName: profile.displayName }
}

async function readResponse(response: Response) {
  const result = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(typeof result.error === 'string' ? result.error : 'Guest entry is unavailable. Please try again.')
  return parseGuestProfile(result.profile)
}

/** Only the host cookie identifies a returning guest. No identity or token is invented locally. */
export async function getGuestProfile(fetcher: FetchProfile = fetch): Promise<GuestProfile | null> {
  const response = await fetcher('/api/guest/profile', { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
  if (response.status === 401) return null
  return readResponse(response)
}

export async function createGuestProfile(displayName = '', fetcher: FetchProfile = fetch): Promise<GuestProfile> {
  const name = displayName.trim()
  if (name && !/^[A-Za-z0-9_]{1,16}$/.test(name)) throw new Error('Use 1–16 letters, numbers, or underscores for your name.')
  return readResponse(await fetcher('/api/guest/profile', {
    method: 'POST', credentials: 'same-origin', cache: 'no-store', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(name ? { displayName: name } : {}),
  }))
}

export function guestConnection(protocol: string, host: string, value: GuestProfile): ConnectOptions {
  const profile = parseGuestProfile(value)
  const { authenticatedAccount, ...connection } = privateConnection(protocol, host)
  // Velocity supplies the immutable UUID and trusted display name. The browser sends only wireName.
  return { ...connection, username: profile.wireName }
}

/** Resolve identity again at the real connection boundary; incoming event/query options are untrusted. */
export async function resolvePrivateConnection(protocol: string, host: string, fetcher: FetchProfile = fetch): Promise<ConnectOptions> {
  const response = await fetcher('/session', { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
  if (!response.ok) throw new Error('Unable to check this browser session. Refresh and try again.')
  const session = await response.json()
  if (session.authMode === 'guest') {
    const profile = await getGuestProfile(fetcher)
    if (!profile) throw new Error('Create your guest character from the Enter JERPG button first.')
    return guestConnection(protocol, host, profile)
  }
  if (session.authMode === 'microsoft') {
    if (session.authorized !== true) throw new Error('Open your private access link before signing in.')
    return privateConnection(protocol, host)
  }
  throw new Error('The host has not enabled an entry mode. Refresh and try again.')
}
